/**
 * Files, and which of them a card shows.
 *
 * Two kinds of file live in one store, and the difference between them is not
 * cosmetic — it is the whole security model.
 *
 *  - A **photo** is evidence (§6): a component removed for failure needs a
 *    teardown *with photos* before it can be scrapped, because "the seal looked
 *    like this" is a sentence a photograph makes and prose does not. A photo is
 *    *rendered* by the browser, so what it actually is must be read out of its
 *    bytes and confined to three formats no browser can be tricked by.
 *  - A **document** is the manufacturing record: the STEP file a part was
 *    machined from, the Gerbers and placement file a board was built from, a
 *    supplier's datasheet. §4.2 asks for exactly this — "`make_external`:
 *    drawing / spec references". A document is *never* rendered. It downloads.
 *
 * Because a document is never rendered, its format does not have to be
 * guessed at, allow-listed, or believed: it is opaque bytes with a name.
 * That is why there is no list of CAD formats in this repository, for the same
 * reason there is no list of part names — the engineers decide which formats
 * their suppliers want, and that answer will change more than once.
 *
 * Nothing here reads or writes bytes.
 */

import type { Instant, UserId } from './types'

export type AttachmentId = string & { readonly __brand: 'AttachmentId' }

/** What a file is a file of. Exactly one, enforced in the schema too. */
export type AttachmentSubject =
  | { readonly kind: 'component_type'; readonly id: string }
  | { readonly kind: 'revision'; readonly id: string }
  | { readonly kind: 'component'; readonly id: string }
  | { readonly kind: 'failure_report'; readonly id: string }
  | { readonly kind: 'squawk'; readonly id: string }
  /** `stepId` says where in the job it was taken. */
  | { readonly kind: 'execution'; readonly id: string; readonly stepId?: string | null }

/**
 * Rendered inline, or downloaded. The one axis that decides how the bytes are
 * served, and therefore the one the caller must state rather than imply.
 */
export type AttachmentPurpose = 'photo' | 'document'

/** The three a browser renders, and the only three `sniffMediaType` returns. */
export type ImageMediaType = 'image/jpeg' | 'image/png' | 'image/webp'
/** Photos still speak in exactly these three. */
export type MediaType = ImageMediaType

export interface Attachment {
  readonly id: AttachmentId
  readonly sha256: string
  readonly purpose: AttachmentPurpose
  /**
   * For a photo, read from the bytes. For a document, what the client claimed —
   * recorded because it is useful, never trusted, and never used to serve.
   */
  readonly mediaType: string
  /**
   * The name the file arrived with. Empty for a photo, always present on a
   * document: in a Gerber set the filenames (`.GTL`, `.GBL`, the drill file)
   * *are* part of the data, and a board house handed six files called `att-3`
   * cannot build the board.
   */
  readonly filename: string
  /**
   * What the file is for — `step`, `gerber`, `bom`, `placement`, `drawing`,
   * `datasheet`. Free text, like `component_types.category` and for the same
   * reason: an enum here would be this file having an opinion about how our
   * suppliers work. The UI suggests; it does not constrain.
   */
  readonly role: string
  readonly byteSize: number
  readonly width: number | null
  readonly height: number | null
  readonly caption: string
  readonly ordinal: number
  readonly uploadedBy: UserId | null
  readonly uploadedAt: Instant
}

/**
 * The photo cap, and why it is where it is.
 *
 * The browser resizes before uploading (`imageFile.ts`), so anything arriving
 * near this has skipped that path — a `curl`, a broken build, an older client.
 * 8 MB is roughly four times what a correctly resized 1400px photo weighs, so
 * it refuses abuse without refusing an honest oddity like a very detailed
 * teardown shot.
 */
export const MAX_BYTES = 8 * 1024 * 1024

/**
 * The document cap, and why it is not larger.
 *
 * A Gerber set, a BOM and a placement file are a few MB together; a single
 * machined part's STEP is a few more. 50 MB clears all of that with room.
 *
 * It is not larger because **the upload is buffered in memory** before it is
 * hashed (`routes/attachments.ts` parses the body as a Buffer). Kronos shares a
 * 2 GB droplet with Cadence and two Postgres instances, so a handful of
 * concurrent 250 MB uploads is not a slow request, it is the box going down and
 * taking the fleet's only record with it. Raising this means streaming to disk
 * while hashing — changing `BlobStore.put` to take a stream — and that is the
 * work to do first, not a bigger number here.
 */
export const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024

export function capFor(purpose: AttachmentPurpose): number {
  return purpose === 'photo' ? MAX_BYTES : MAX_DOCUMENT_BYTES
}

export type UploadRefusal =
  | { readonly ok: true; readonly mediaType: string; readonly purpose: AttachmentPurpose }
  | {
      readonly ok: false
      readonly reason: 'empty' | 'too_large' | 'not_an_image' | 'no_filename'
      readonly detail: string
    }

/**
 * What these bytes actually are, read from the bytes.
 *
 * **Never trust the declared `Content-Type`.** A phone that mislabels a HEIC as
 * a JPEG produces a row claiming to be an image and a file no browser renders,
 * and the failure shows up weeks later as a broken picture on a teardown nobody
 * can re-photograph. Four magic numbers is a cheap way to be sure.
 */
export function sniffMediaType(bytes: Uint8Array): ImageMediaType | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg'
  }
  const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
  if (bytes.length >= 8 && PNG.every((b, i) => bytes[i] === b)) return 'image/png'
  // RIFF....WEBP
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return 'image/webp'
  }
  return null
}

/**
 * Whether these bytes may be uploaded for this purpose.
 *
 * A photo is checked against its bytes because it will be rendered. A document
 * is not checked against its bytes at all, because it will not be — it is
 * checked for a *name*, which is the thing a document cannot be useful without.
 */
export function checkUpload(
  bytes: Uint8Array,
  purpose: AttachmentPurpose = 'photo',
  filename = '',
): UploadRefusal {
  if (bytes.length === 0) return { ok: false, reason: 'empty', detail: 'that file is empty' }

  const cap = capFor(purpose)
  if (bytes.length > cap) {
    return {
      ok: false,
      reason: 'too_large',
      detail:
        purpose === 'photo'
          ? `that file is ${Math.round(bytes.length / 1024 / 1024)} MB; the limit is ${cap / 1024 / 1024} MB, and the app normally shrinks a photo before sending it`
          : `that file is ${Math.round(bytes.length / 1024 / 1024)} MB; the limit is ${cap / 1024 / 1024} MB`,
    }
  }

  if (purpose === 'document') {
    // A document is opaque by design, so its name is the only thing that says
    // what it is. `gerbers.zip` and `top-copper.GTL` mean something to the
    // board house; `att-91f3` means nothing to anybody.
    if (safeFilename(filename) === '') {
      return { ok: false, reason: 'no_filename', detail: 'a file needs a name' }
    }
    return { ok: true, mediaType: normalizeMediaType(''), purpose }
  }

  const mediaType = sniffMediaType(bytes)
  if (mediaType === null) {
    return {
      ok: false,
      reason: 'not_an_image',
      detail:
        'that is not a JPEG, PNG or WebP — whatever it says it is. Add it as a file instead, and it will keep its name and download rather than being shown.',
    }
  }
  return { ok: true, mediaType, purpose }
}

/**
 * The name a file keeps, with everything dangerous taken out of it.
 *
 * This value is echoed into a `Content-Disposition` header, so a newline in it
 * is not a cosmetic problem — it is header injection, and the response after
 * the split is attacker-written. It also reaches the DOM. Both are why this is
 * a tested pure function rather than a template string at the call site.
 *
 * The directory part goes too: a browser sends only a basename, but `curl -T`
 * and a scripted import do not, and `../../` in a displayed name is at best
 * confusing and at worst believed.
 */
export function safeFilename(raw: string): string {
  const basename = raw.split(/[/\\]/).pop() ?? ''
  const cleaned = basename
    // Control characters, quotes and backslash: the header's own metacharacters.
    //
    // `no-control-regex` is off for this line rather than worked around. The
    // rule exists to catch a control character typed into a pattern by
    // accident; here matching them is the entire point, and the alternative
    // spellings that satisfy the linter all describe the same set less
    // legibly.
    // oxlint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f"\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  // A name that is only dots addresses a directory rather than naming a file.
  if (/^\.+$/.test(cleaned)) return ''
  return cleaned.slice(0, 120)
}

/**
 * A media type we are willing to write down, or `application/octet-stream`.
 *
 * Only ever *metadata* for a document — the served type is decided by
 * `purpose`, not by this. Normalising anyway keeps the column to a shape the
 * schema can CHECK, so a client that sends a header full of junk cannot store
 * it and hand it to the next reader.
 */
export function normalizeMediaType(raw: string): string {
  const trimmed = raw.split(';')[0]?.trim().toLowerCase() ?? ''
  return /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/.test(trimmed)
    ? trimmed
    : 'application/octet-stream'
}

export function isImage(mediaType: string): mediaType is ImageMediaType {
  return mediaType === 'image/jpeg' || mediaType === 'image/png' || mediaType === 'image/webp'
}

// --- splitting the two apart -------------------------------------------------

/**
 * The photos in a mixed list, and the documents in one.
 *
 * Both halves share a subject and an ordinal sequence, because they are the
 * same store — but no screen ever wants both at once. A photo strip that drew a
 * STEP file would draw a broken image; a file list that drew teardown photos
 * would bury the Gerbers.
 */
export function photos(list: readonly Attachment[]): readonly Attachment[] {
  return ordered(list.filter((a) => a.purpose === 'photo'))
}

export function documents(list: readonly Attachment[]): readonly Attachment[] {
  return ordered(list.filter((a) => a.purpose === 'document'))
}

// --- which photo a card shows -----------------------------------------------

/**
 * The picture to put on a component's card, and where it came from.
 *
 * **Falls back up the chain: this part → the revision it was built to → its
 * type.** Almost no individual actuator will ever be photographed, and almost
 * every *type* will be — so a card with nothing of its own should still show
 * what the thing looks like rather than a grey box. When a part does have its
 * own photo, that wins, because a photo of this arm beats a photo of arms in
 * general.
 *
 * The `from` field is not decoration: a page showing a type photo on an
 * instance is making a claim ("this is roughly what it looks like") that is
 * weaker than the one it makes about its own, and the UI says which.
 */
export type ImageSource = 'component' | 'revision' | 'component_type'

export interface ResolvedImage {
  readonly attachment: Attachment
  readonly from: ImageSource
}

export function imageFor(chain: {
  readonly component?: readonly Attachment[] | undefined
  readonly revision?: readonly Attachment[] | undefined
  readonly componentType?: readonly Attachment[] | undefined
}): ResolvedImage | null {
  const order: readonly [ImageSource, readonly Attachment[] | undefined][] = [
    ['component', chain.component],
    ['revision', chain.revision],
    ['component_type', chain.componentType],
  ]
  for (const [from, list] of order) {
    const first = primary(list ?? [])
    if (first) return { attachment: first, from }
  }
  return null
}

/**
 * The one a card shows: lowest ordinal, ties broken by upload time then id.
 *
 * Arbitrary is fine; unstable is not. A card that shows a different photo on
 * each load is one people stop reading.
 *
 * **Documents are not candidates.** A revision whose first upload was its STEP
 * file must still show a photograph on its card, not a broken image where the
 * CAD file would have been drawn.
 */
export function primary(list: readonly Attachment[]): Attachment | null {
  const candidates = list.filter((a) => a.purpose === 'photo')
  if (candidates.length === 0) return null
  return [...candidates].sort(byOrdinal)[0] ?? null
}

export function ordered(list: readonly Attachment[]): readonly Attachment[] {
  return [...list].sort(byOrdinal)
}

function byOrdinal(a: Attachment, b: Attachment): number {
  if (a.ordinal !== b.ordinal) return a.ordinal - b.ordinal
  if (a.uploadedAt !== b.uploadedAt) return a.uploadedAt < b.uploadedAt ? -1 : 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/**
 * The path a content-addressed file sits at, as two levels of fan-out.
 *
 * `ab/cd/abcdef…` rather than one flat directory: ext4 copes with a large
 * directory but `ls` does not, and the first time somebody has to look at this
 * store by hand will be the day something has gone wrong.
 */
export function blobPath(sha256: string): string {
  if (!/^[0-9a-f]{64}$/.test(sha256)) {
    throw new Error(`not a sha256: ${sha256}`)
  }
  return `${sha256.slice(0, 2)}/${sha256.slice(2, 4)}/${sha256}`
}

export function extensionFor(mediaType: ImageMediaType): string {
  switch (mediaType) {
    case 'image/jpeg':
      return 'jpg'
    case 'image/png':
      return 'png'
    case 'image/webp':
      return 'webp'
  }
}

/**
 * What to call the file on the way out.
 *
 * A document keeps the name it arrived with — that is the point of storing it.
 * A photo has no name of its own, so it is named after its id, which is at
 * least unique and sorts with its siblings.
 */
export function downloadName(a: Attachment): string {
  if (a.purpose === 'document') {
    const safe = safeFilename(a.filename)
    if (safe !== '') return safe
  }
  return `${a.id}.${isImage(a.mediaType) ? extensionFor(a.mediaType) : 'bin'}`
}
